import type { Action, Reducer } from './collections.types';
import { REQUEST_RESOLVED, ITEM_INSERTED } from './action-types';

/**
 * Action that contains sibling data nested by entity type
 */
interface SiblingAction extends Action {
  payload: {
    [entityType: string]: Record<string, any>;
  };
}

/**
 * Type guard to check if action contains sibling data for the specified type
 */
const hasSiblingData = (
  action: Action,
  type: string
): action is SiblingAction =>
  !!(
    action.payload &&
    typeof action.payload === 'object' &&
    action.payload[type]
  );

/**
 * Higher-order reducer that extracts sibling entities from action payloads
 * while delegating all other operations to the wrapped reducer.
 *
 * Used when API responses contain multiple entity types and you need to
 * extract a specific entity type from the nested payload structure.
 *
 * @param reducer - Base reducer to delegate normal operations to
 * @param type - Entity type to extract from action payloads
 * @returns Enhanced reducer that handles sibling entity resolution
 */
export default function resolveSiblings<S>(
  reducer: Reducer<S>,
  type: string
): Reducer<S> {
  return (state: S | undefined, action: Action): S => {
    if (hasSiblingData(action, type)) {
      const items = action.payload[type];

      if (action.type === REQUEST_RESOLVED) {
        // Replace entire state with resolved siblings
        return items as S;
      }

      if (action.type === ITEM_INSERTED) {
        // Merge siblings into existing state
        return { ...(state || ({} as S)), ...items } as S;
      }
    }

    // Delegate to wrapped reducer for all other actions
    return reducer(state, action);
  };
}
